/**
 * Admin 认证状态 + hub API client(浏览器端)。
 *
 * - token 存 localStorage(简单,够内部工具用)
 * - 每次请求自动附 Authorization: Bearer
 * - 401 自动清 token 踢回登录页
 */

const TOKEN_KEY = "mhwork-hub-admin-token";

export interface AdminUser {
  id: string;
  email: string;
  displayName: string | null;
  role: "owner" | "publisher" | "reviewer" | "viewer";
}

const ADMIN_KEY = "mhwork-hub-admin-user";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getCurrentAdmin(): AdminUser | null {
  const raw = localStorage.getItem(ADMIN_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AdminUser;
  } catch {
    return null;
  }
}

export function setAuth(token: string, admin: AdminUser) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(ADMIN_KEY, JSON.stringify(admin));
}

export function clearAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(ADMIN_KEY);
}

/** hub API base URL(dev 默认走本地 wrangler dev,prod 换线上) */
const API_BASE =
  (import.meta.env.VITE_HUB_API_BASE as string | undefined) ?? "http://localhost:8787";

export interface HubFetchOptions extends RequestInit {
  /** 传 false 表示不要求 token */
  requireAuth?: boolean;
}

export async function hubFetch<T = unknown>(
  path: string,
  opts: HubFetchOptions = {},
): Promise<T> {
  const { requireAuth = true, headers, ...rest } = opts;
  const h = new Headers(headers);
  if (!h.has("Content-Type") && rest.body) h.set("Content-Type", "application/json");

  const token = getToken();
  if (token) h.set("Authorization", `Bearer ${token}`);
  else if (requireAuth) throw new Error("unauthenticated");

  const res = await fetch(`${API_BASE}${path}`, { ...rest, headers: h });
  if (res.status === 401) {
    clearAuth();
    if (typeof window !== "undefined") window.location.href = "/login";
    throw new Error("unauthorized");
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
  }
  // 允许 204 / 无 body
  const ct = res.headers.get("content-type") ?? "";
  if (!ct.includes("json")) return undefined as T;
  return (await res.json()) as T;
}
